import { Value, ViewableQuestion } from '../types/global';
import { FormulaStore, GraphState, Option, ValueStore } from '../types/graph';
import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarWeeks,
  differenceInCalendarYears,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  format,
  getYear,
  startOfDay,
  startOfToday,
} from 'date-fns';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import config from '../config';

export const assertionKeys = {
  sexMale: '[Personne: #x‹]-(02.a_genre)->[Genre: Masculin]',
  sexFemale: '[Personne: #x‹]-(02.a_genre)->[Genre: Féminin]',
};

export const valueKeys = {
  firstName:
    '[Personne: #x‹]-(01.DESCR)->[Prénom: #‹]-(0.a_pour_valeur)->[Valeur: *]',
  lastName:
    '[Personne: #x‹]-(01.DESCR)->[Nom: #‹]-(0.a_pour_valeur)->[Valeur: *]',
  date: '[Moment: #t0‹]-(a_date)->[Date]-(0.a_pour_valeur)->[Valeur: *]',
  dateOfBirth:
    '[Personne: #x‹]-(00.date_naissance)->[Naissance: #‹]-(0.a_pour_valeur)->[Valeur: *]',
};

export const valueRegex = /\[Valeur: (.+?)\](?:-\(unité\)->\[Unité: (.+?)\])?/;
export const standardValue = '[Valeur: *]';
const valueGraphRegex = /\{(\*)?(.+?)(?:-\(unité\)->\[Unité: (.+?)\])?(\*)?\}/g;

const helpers = {
  round: Math.round,
  pow: Math.pow,
  ZeroSiNA: (value: Value) => (!isNaN(value as number) ? Number(value) : 0),
  isNumber: (value: Value) => !isNaN(value as number),
  answered: (value: Value) => (value !== undefined ? 1 : 0),
  not: (value: Value) => !value,
  toBoolean: (value: Value) => Boolean(value),
  isNil,
  differenceInYears,
  differenceInMonths,
  differenceInWeeks,
  differenceInDays,
  differenceInCalendarYears,
  differenceInCalendarMonths,
  differenceInCalendarWeeks,
  differenceInCalendarDays,
  startOfToday: () => startOfToday().getTime(),
  startOfDay: (date: Date | number) => startOfDay(date).getTime(),
  setTimeOnDate: (date: Date, time: Date) => {
    const d = new Date(date);
    const t = new Date(time);
    d.setHours(
      t.getHours(),
      t.getMinutes(),
      t.getSeconds(),
      t.getMilliseconds(),
    );
    return d.getTime();
  },
  getYear: (date: number) => getYear(date),
  getCurrentYear: () => getYear(new Date()),
  dateToText: (date: number) => format(date, 'dd/MM/yyyy'),
  randomInt: (min: number, max: number) =>
    Math.floor(Math.random() * (max - min + 1)) + min,
};

export const evaluate = (
  graphValues: ValueStore,
  formulas: FormulaStore,
  clause: string,
): Value => {
  const reformatted = clause.match(/^\[(.+)\]$/);
  const withoutValues = reformatted?.[1] ?? clause;

  if (!withoutValues) return undefined;

  const values: Value[] = [];

  if (withoutValues === '{the system date}')
    return format(new Date(), 'd MMMM y');

  if (withoutValues === '{the system time}')
    return format(new Date(), "HH'h'mm");

  try {
    let i = 0;

    const before = withoutValues.replaceAll(
      /\{\*.*?\[Formule: (.+?)\].*?\*\}/g,
      (_, formula) => formulas[formula] ?? 'undefined',
    );
    const toEvaluate = before.replaceAll(
      valueGraphRegex,
      (_, partial, a, unit) => {
        const assertion = a.trim();
        const key = partial
          ? Object.keys(graphValues).find((v) => v.includes(assertion))
          : assertion;

        if (!unit || (key && graphValues[key].unit?.plural === unit)) {
          values[i] = graphValues[key]?.value;
        } else {
          values[i] = undefined;
        }
        return `values[${i++}]`;
      },
    );

    const calculator = new Function(
      ...Object.keys(helpers),
      'values',
      `return (${toEvaluate})`,
    );
    const result = calculator(...Object.values(helpers), values);
    if (
      result !== true &&
      result !== false &&
      isNaN(result) &&
      !(typeof result === 'string')
    )
      return undefined;
    return result;
  } catch (e) {
    if (config.debug.evaluate) console.error(e, clause);
    return undefined;
  }
};

export const checkClause =
  (
    graph: GraphState,
    formulas: FormulaStore,
    excludeQuestionId?: ViewableQuestion['extId'],
  ) =>
  (conditionString: string) => {
    const effectiveOptions: Record<string, boolean | undefined> = {
      ...graph.options,
      ...graph.personalizationFlags,
    };
    const options = Object.keys(effectiveOptions).filter(
      (k) => effectiveOptions[k],
    );

    const ownAssertions = excludeQuestionId
      ? graph.assertionsByQuestion[excludeQuestionId] ?? []
      : [];

    const assertionsToConsider = ownAssertions.length
      ? graph.assertions.filter((a) => !ownAssertions.includes(a))
      : graph.assertions;

    // graph.values is keyed by the same strings as assertionsByQuestion
    const valuesToConsider = ownAssertions.length
      ? omit(graph.values, ...ownAssertions)
      : graph.values;

    return conditionString.split('<-<ET>->').every((rawC) => {
      const c = rawC.trim();
      if (!c) return true;

      const negationMatch = c.match(/(^NOT )?(.+)/) ?? [];
      const [, not, condition] = negationMatch;

      const sectionMatch = condition.match(/\[>(.+)<]/);
      if (sectionMatch) {
        const [assertion, section] = sectionMatch;

        const option = section.toUpperCase();
        const res =
          options.includes(option as Option) ||
          graph.mode === option ||
          assertionsToConsider.includes(assertion);
        return not ? !res : res;
      }

      const [standard, evaluated] = condition.split('<EVALUE>->');
      if (evaluated)
        return Boolean(evaluate(valuesToConsider, formulas, evaluated));

      return not
        ? !assertionsToConsider.includes(standard)
        : assertionsToConsider.includes(standard);
    });
  };

export const checkCondition = (
  graph: GraphState,
  formulas: FormulaStore,
  conditionString: string | undefined,
  excludeQuestionId?: ViewableQuestion['extId'],
): boolean => {
  if (!conditionString) return true;
  const conditions = conditionString.split('!!').map((c) => c.trim());

  const check = checkClause(graph, formulas, excludeQuestionId);

  return conditions.every((c, i) => (i % 2 === 0 ? check(c) : !check(c)));
};

/**
 * Object-level `-<CONDITION>->` gate on a Question or a Response. The lines OR
 * together — one holding line is enough — and an object carrying no line is
 * never gated. Accepted wart: a line left blank passes on its own and so opens
 * the gate for the whole object; only a `-<CONDITION>->` written with nothing
 * but a space after it compiles to one, and the KB has none.
 */
export const checkObjectConditions = (
  conditions: string[] | undefined,
  check: (condition: string) => boolean,
): boolean => !conditions?.length || conditions.some(check);

export const sortQuestions = (
  input: ViewableQuestion[],
  insertionOrder: ViewableQuestion['extId'][],
  sectionAssignments: Record<ViewableQuestion['extId'], string>,
  targetSection: string,
): ViewableQuestion[][] => {
  const sectionQuestions = input.filter(
    (q) =>
      q.section === targetSection ||
      (!q.section && q.after && sectionAssignments[q.extId] === targetSection),
  );

  // 4. Build a map of questions by their after relationships
  const questionsByAfter = sectionQuestions.reduce<
    Record<ViewableQuestion['extId'], ViewableQuestion[]>
  >((acc, q) => {
    if (q.after) {
      if (acc[q.after]) acc[q.after].push(q);
      else acc[q.after] = [q];
    }
    return acc;
  }, {});

  const unwindTree = (q: ViewableQuestion): ViewableQuestion[] => {
    if (!questionsByAfter[q.extId]) return [q];
    return [
      q,
      ...questionsByAfter[q.extId]
        .sort(
          (a, b) =>
            insertionOrder.indexOf(b.extId) - insertionOrder.indexOf(a.extId),
        )
        .flatMap(unwindTree),
    ];
  };

  const heads = sectionQuestions.filter(
    (q) => !q.after || !sectionQuestions.find((r) => r.extId === q.after),
  );

  return heads
    .map((head) => unwindTree(head))
    .sort((a, b) => b.length - a.length);
};
